import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import styled from 'styled-components';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { observer } from 'mobx-react-lite';

import { MenuSectionType } from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { PersonalizedProducts } from 'src/components/carousels/products-carousels/personalized-products-carousel';

import { destinationField } from '../../fields/widgets/destination-field';
import { BRAND_COLOR } from '../../fields/tokens';
import { interpolateFromPuck } from '../../interpolation';
import { brandPickerField } from '../../fields/widgets/brand-picker-field';
import { collectionPickerField } from '../../fields/widgets/collection-picker-field';
import { productPickerField } from '../../fields/widgets/product-picker-field';
import { selectField } from '../../fields/widgets/select-field';
import { toggleField } from '../../fields/widgets/toggle-field';
import { renderFieldGroups } from '../../fields/field-groups';
import type { FieldGroupDefinition } from '../../fields/field-groups';
import {
  carouselTypeDefinition,
  CATEGORY_OPTIONS,
  COLLECTION_SORT_OPTIONS,
  PRODUCT_CAROUSEL_TYPE_OPTIONS,
  resolveCarouselHeading,
  resolveSubcategory,
  subcategoryOptionsForCategory,
} from './carousel-types';
import type { ProductCarouselProps } from './carousel-types';
import { carouselTypeRenderer } from './type-renderers';
import type { ProductCarouselRenderProps } from './type-renderers';

/**
 * The consolidated Product Carousel: one block whose remaining fields follow from a Type
 * selection, replacing the ten per-type carousel blocks the menu landing model used to
 * register.
 *
 * Model-neutral by design, and registered by both the menu landing and standalone page
 * builders — the same carousel is a Page Section wherever an author drops it. It needs a
 * resolved dispensary, which every context provides: published pages carry a `cName` on
 * the route, and the editor canvas gets one seeded by the "Previewing as" control.
 *
 * Types are wired up a ticket at a time so each one's field set and its store-scoping
 * consequences get reviewed on their own. Live today: the platform-decided types in
 * ./type-renderers (ECOMM-12683), the category/subcategory types (ECOMM-12684), brand
 * (ECOMM-12685 — reviewed and merged as its own unit in #22049; its diff rides along here
 * because that PR was based on this branch, so both arrive together), and collection
 * (ECOMM-13197).
 *
 * Brand's store-scoping consequence is the one worth knowing: only *global* brands are
 * offered, because a store-local `brandId` renders an empty carousel on sibling stores.
 * See `fields/widgets/brand-picker-widget.tsx` for the full rationale and the client-side
 * defence in depth.
 *
 * Still inert, and rendering the editor placeholder: custom.
 */

const EditorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-placeholder-color, #646d72);
  font-size: 14px;
`;

const PlaceholderHeading = styled.span`
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 600;
  color: var(--dcm-cms-placeholder-heading-color, #393c40);
`;

/** Shown in place of the resolved heading when there is none to show. */
const UNTITLED_PLACEHOLDER_HEADING = 'Product Carousel';

// Stable references. A fresh `[]` as a destructuring default is a new value on every render, which
// would defeat downstream memoisation and make the derived-parity guard compare unequal objects.
const EMPTY_PRODUCT_IDS: string[] = [];
const EMPTY_PRODUCT_NAMES: string[] = [];

// A carousel's auto side margins disable stretching as a Section flex child. Give it a
// full-width block parent so its scroll row cannot size the Section or a Grid cell.
const CarouselSlot = styled.div`
  width: 100%;
  min-width: 0;

  &:empty {
    display: none;
  }
`;

/**
 * Editor-only wrapper for a type that DOES have a renderer.
 *
 * Every carousel hides itself when it has nothing to show — no matching products for
 * the previewed store, or a visibility guard like "signed out" for Recently Viewed. On
 * the storefront that is exactly right. In the editor it collapsed the block to a
 * zero-height box: Puck hit-tests on the bounding rect, so the block became impossible
 * to select or drag, and to an author it simply vanished the moment they chose that
 * type — with no hint that the choice had worked and the store just had no products.
 *
 * The notice below is always rendered and hides itself the moment the carousel renders
 * anything: the carousel's own DOM becomes a sibling, so the notice stops being an only
 * child. That keeps it self-correcting — it disappears on its own once products arrive,
 * and even while the carousel is still showing loading skeletons, which are real
 * elements — without having to thread an "am I empty?" signal back out of carousels that
 * decide it internally.
 *
 * Deliberately a real element rather than `::after` generated content: it is readable by
 * assistive tech, and it carries no CSS string to escape. If the rule below ever failed
 * to apply, the notice would show on a working carousel — visually noisy, but the block
 * stays visible and selectable, which is the safer way for this to break.
 *
 * The published wrapper has no notice and hides itself when the carousel renders nothing.
 */
const EditorCarouselSlot = styled(CarouselSlot)`
  & > [data-empty-notice]:not(:only-child) {
    display: none;
  }
`;

const EmptyCarouselNotice = styled.p`
  margin: 0;
  padding: 24px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-placeholder-color, #646d72);
  font-size: 14px;
`;

// A delegate can return null after loading or a visibility guard. Keep its optional
// header out of layout too, without duplicating the delegate's product query.
const LinkedCarouselSlot = styled(CarouselSlot)`
  &:has([data-carousel-header]:only-child) {
    display: none;
  }

  > [data-carousel-header]:last-child {
    display: none;
  }
`;

const EditorLinkedCarouselSlot = styled(LinkedCarouselSlot)`
  > [data-empty-notice]:not(:nth-last-child(2)) {
    display: none;
  }
`;

/**
 * Matches the width and centering of the carousel's own wrapper (carousel-v2's Wrapper), which
 * caps at the site width and sits centered in the slot. Without the same cap the header spans the
 * whole slot, leaving the title and link far outside the cards below. The carousel's items start
 * at the wrapper edge, so this header takes no horizontal padding and lines up with the first card.
 */
const CarouselHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  max-width: var(--dct--customized-maxSiteWidth);
  margin: 0 auto;
  padding: 0 0 16px;
`;

const CarouselHeading = styled.h2`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--dcm-cms-product-carousel-heading-color, var(--dcm-section-title-color, #393c40));
  font-size: var(--dcm-cms-product-carousel-heading-font-size, 20px);
  line-height: 1.2;
  font-weight: 700;
`;

const ViewAllLink = styled.a`
  margin-left: auto;
  overflow-wrap: anywhere;
  color: var(--dcm-cms-product-carousel-link-color, ${BRAND_COLOR});
  font-size: var(--dcm-cms-product-carousel-link-font-size, 14px);
  text-decoration: underline;

  &:focus-visible {
    outline: var(--dcm-cms-product-carousel-link-focus-outline, 3px solid ${BRAND_COLOR});
    outline-offset: 2px;
  }
`;

function RecommendedProducts({ heading }: ProductCarouselRenderProps): JSX.Element {
  const { dispensary } = useDispensary();
  const { personalizedProductsQuery } = useProductsForHomePage({ dispensaryId: dispensary?.id ?? '' });
  return <PersonalizedProducts personalizedProductsQuery={personalizedProductsQuery} title={heading} />;
}

const RecommendedForYouRender = observer(
  (props: ProductCarouselRenderProps): JSX.Element => {
    const { isLoggedIn } = useUser();
    const { isKiosk } = useUI();
    const isKioskProEnabled = useKioskPro();
    if (!isLoggedIn || (isKiosk && !isKioskProEnabled)) {
      return <></>;
    }
    return <RecommendedProducts {...props} />;
  }
);

const PRODUCT_CAROUSEL_FIELD_GROUPS: readonly FieldGroupDefinition<keyof ProductCarouselProps>[] = [
  { label: 'Content', fields: ['heading', 'showHeading'] },
  {
    label: 'Products',
    fields: [
      'carouselType',
      'category',
      'subcategory',
      'brandId',
      'brandName',
      'collectionSlug',
      'collectionTitle',
      'sortBy',
      'productIds',
      'productNames',
    ],
  },
  { label: 'Link', fields: ['viewAllLabel', 'viewAllHref'] },
];

export const productCarouselConfig: ComponentConfig<ProductCarouselProps> = {
  label: 'Product Carousel',
  ai: {
    instructions:
      'A row of products on the storefront. Set the optional heading, then choose Products to show; that choice determines which dependent settings apply.',
  },
  fields: {
    heading: {
      type: 'text',
      label: 'Heading',
      ai: {
        instructions:
          'Leave empty to use the heading generated from the selected type. Supports {{name}} and {{address}} placeholders.',
      },
    },
    showHeading: toggleField(
      'Show heading',
      'Set to false to render the carousel with no heading at all, which is different from leaving the Heading field empty.',
      undefined,
      { defaultOn: true }
    ),
    viewAllLabel: { type: 'text', label: 'View all label' },
    viewAllHref: destinationField('View all destination', {
      instructions:
        'Optional destination for the carousel header link. Leave empty to keep the original carousel header.',
    }),
    carouselType: selectField(
      'Products to show',
      PRODUCT_CAROUSEL_TYPE_OPTIONS,
      'Select the source for this carousel. Additional settings appear below when that source needs them.',
      'Recommended For You is personalized to the shopper and does not appear for signed-out shoppers or when recommendations are unavailable.'
    ),
    // category, subcategory, brandId, and brandName are declared here to satisfy
    // TypeScript's requirement that all props in ProductCarouselProps have a
    // corresponding field entry. resolveFields controls when they actually appear in
    // the editor panel: only for the types that declare them in extraFields.
    category: selectField('Category', CATEGORY_OPTIONS, 'The product category this carousel shows.'),
    subcategory: selectField('Subcategory', [], 'The subcategory to feature. Options depend on the selected Category.'),
    brandId: brandPickerField,
    brandName: { type: 'text', label: 'Brand Name', ai: { exclude: true } },
    collectionSlug: collectionPickerField,
    collectionTitle: { type: 'text', label: 'Collection Name', ai: { exclude: true } },
    sortBy: selectField('Sort by', COLLECTION_SORT_OPTIONS, 'How products are ordered within the collection.'),
    productIds: productPickerField,
    // Companion display list the picker maintains alongside productIds; stripped from the panel in
    // resolveFields so an author cannot edit the names out of step with the ids.
    productNames: { type: 'textarea', label: 'Product Names', ai: { exclude: true } },
  },
  renderFields: ({ fields }) => renderFieldGroups(fields, PRODUCT_CAROUSEL_FIELD_GROUPS),
  /**
   * Drops a stored subcategory that is not valid for the current category.
   *
   * `resolveFields` rebuilds the Subcategory *field* when the category changes, but it cannot
   * touch stored *props* -- so the previous category's value survived and rendered through
   * `selectField`'s unlisted-value branch as `Custom (bulk-flower)`. That branch exists for
   * genuinely hand-authored values, so it cannot itself distinguish "deliberate" from "stale";
   * the carousel owns that judgement because only it knows subcategories are scoped by category.
   *
   * The renderer already re-resolves defensively (`SubcategoryRender`), so this never changed
   * what shoppers saw -- it only stopped the panel from asserting a value the block would ignore.
   */
  resolveData: ({ props }) => {
    if (!props.subcategory) {
      return { props };
    }
    const validSubcategory = resolveSubcategory(props.category, props.subcategory);
    if (validSubcategory === props.subcategory) {
      return { props };
    }
    return { props: { ...props, subcategory: '' } };
  },
  /**
   * Merges per-type `extraFields` into the field set whenever the type changes.
   *
   * Signature: `(data, { fields }) => Fields<ProductCarouselProps>`
   *
   * The mechanism is declarative: each type definition contributes an optional
   * `extraFields` map; `resolveFields` just spreads whatever the current type declares.
   * Brand/Collection/Custom (ECOMM-12685/86/87) add their own `extraFields` to their
   * type definitions without touching this function at all.
   *
   * For the subcategory type, the subcategory field's option list is rebuilt from the
   * currently selected category each time either changes, keeping the dropdown in sync.
   */
  resolveFields: (data, { fields }) => {
    const typeDef = carouselTypeDefinition(data.props.carouselType);
    const extra = typeDef?.extraFields ?? {};

    // Rebuild the subcategory field's options from the current category so the dropdown
    // always reflects what is valid for the selected category.
    const subcategoryOptions = subcategoryOptionsForCategory(data.props.category);
    const resolvedExtra =
      'subcategory' in extra
        ? {
            ...extra,
            subcategory: selectField(
              'Subcategory',
              subcategoryOptions,
              'The subcategory to feature. Options depend on the selected Category.'
            ),
          }
        : extra;

    // `fields` (the static config) includes category, subcategory, brandId, and
    // brandName to satisfy TypeScript, but they must only appear in the panel for
    // types that declare them in extraFields. Destructure them out of the base so
    // they are never present unless the type re-introduces them via resolvedExtra.
    const {
      brandId: _brand,
      brandName: _brandName,
      category: _cat,
      collectionSlug: _collectionSlug,
      collectionTitle: _collectionTitle,
      sortBy: _sortBy,
      productIds: _productIds,
      productNames: _productNames,
      subcategory: _sub,
      ...baseFields
    } = fields;
    // Cast: TypeScript requires the full Fields<ProductCarouselProps> shape, but
    // Puck correctly treats the returned set as the complete field list for the
    // editor panel — category/subcategory are absent for types that do not need them.
    if (data.props.carouselType === MenuSectionType.recommendedForYou) {
      const {
        showHeading: _showHeading,
        viewAllLabel: _viewAllLabel,
        viewAllHref: _viewAllHref,
        ...fieldsForYou
      } = baseFields;
      return { ...fieldsForYou, ...resolvedExtra } as Fields<ProductCarouselProps>;
    }
    return { ...baseFields, ...resolvedExtra } as Fields<ProductCarouselProps>;
  },
  defaultProps: {
    brandId: '',
    brandName: '',
    category: '',
    carouselType: '',
    collectionSlug: '',
    collectionTitle: '',
    heading: '',
    productIds: EMPTY_PRODUCT_IDS,
    productNames: EMPTY_PRODUCT_NAMES,
    showHeading: true,
    sortBy: '',
    subcategory: '',
    viewAllHref: '',
    viewAllLabel: 'View all',
  },
  render: ({
    brandId = '',
    brandName = '',
    category = '',
    carouselType = '',
    collectionSlug = '',
    collectionTitle = '',
    heading = '',
    productIds = EMPTY_PRODUCT_IDS,
    productNames = EMPTY_PRODUCT_NAMES,
    showHeading = true,
    sortBy = '',
    subcategory = '',
    viewAllHref = '',
    viewAllLabel = 'View all',
    puck,
  }) => {
    // Personalized Products owns its header. Ignore stored showHeading and View all
    // values for this type only; they remain saved and apply again if the type changes.
    const isRecommendedForYou = carouselType === MenuSectionType.recommendedForYou;
    const authoredHeading = resolveCarouselHeading({
      brandId,
      brandName,
      category,
      carouselType,
      collectionSlug,
      collectionTitle,
      heading,
      productIds,
      productNames,
      showHeading: isRecommendedForYou ? true : showHeading,
      sortBy,
      subcategory,
    });
    // Interpolation has to preserve `undefined` rather than collapse it to '', since
    // "no heading" and "empty heading" mean different things downstream.
    const resolvedHeading = authoredHeading === undefined ? undefined : interpolateFromPuck(authoredHeading, puck);

    const typeDef = carouselTypeDefinition(carouselType);
    const typeLabel = typeDef?.label;
    const TypeRender = isRecommendedForYou ? RecommendedForYouRender : carouselTypeRenderer(carouselType);

    // A type may require author-supplied fields (category, subcategory, brand, …) before
    // it can issue a meaningful product query. When the predicate says "not yet configured",
    // skip the delegate carousel entirely — on the storefront to avoid a doomed query, and
    // in the editor to give the author an actionable placeholder instead of a misleading
    // "this store has nothing to show" notice.
    const configured = typeDef?.isConfigured
      ? typeDef.isConfigured({
          brandId,
          brandName,
          category,
          carouselType,
          collectionSlug,
          collectionTitle,
          heading,
          productIds,
          productNames,
          showHeading,
          sortBy,
          subcategory,
        })
      : true;

    if (TypeRender && !configured) {
      if (!puck.isEditing) {
        return <></>;
      }
      const missingFields = typeDef?.missingFieldsMessage ?? 'the required fields';
      return (
        <EditorPlaceholder>
          <PlaceholderHeading>{resolvedHeading ?? UNTITLED_PLACEHOLDER_HEADING}</PlaceholderHeading>
          <span>
            {`Choose ${missingFields} in the settings panel.`} This block will not render on the published page.
          </span>
        </EditorPlaceholder>
      );
    }

    if (TypeRender) {
      const safeHref = typeof viewAllHref === 'string' && viewAllHref.trim() !== '' ? sanitizeUrl(viewAllHref) : '';
      const hasAuthoredLink = !isRecommendedForYou && safeHref !== '' && safeHref !== 'about:blank';
      const resolvedLabel = interpolateFromPuck(typeof viewAllLabel === 'string' ? viewAllLabel : '', puck);
      const label = resolvedLabel.trim() !== '' ? resolvedLabel : 'View all';
      const carousel = (
        <TypeRender
          heading={resolvedHeading}
          hideHeader={hasAuthoredLink || undefined}
          category={category}
          subcategory={subcategory}
          brandId={brandId}
          collectionSlug={collectionSlug}
          sortBy={sortBy}
          productIds={productIds}
        />
      );
      if (hasAuthoredLink) {
        const Slot = puck.isEditing ? EditorLinkedCarouselSlot : LinkedCarouselSlot;
        return (
          <Slot>
            {puck.isEditing && (
              <EmptyCarouselNotice data-empty-notice>
                {`${
                  typeLabel ?? 'This carousel'
                } has nothing to show for this store, so it won't appear on the published page.`}
              </EmptyCarouselNotice>
            )}
            <CarouselHeader data-carousel-header>
              {resolvedHeading && <CarouselHeading>{resolvedHeading}</CarouselHeading>}
              <ViewAllLink href={safeHref}>{label}</ViewAllLink>
            </CarouselHeader>
            {carousel}
          </Slot>
        );
      }
      if (!puck.isEditing) {
        return <CarouselSlot>{carousel}</CarouselSlot>;
      }
      const emptyMessage = typeDef?.emptyMessage
        ? `${typeDef.emptyMessage} This block will not appear on the published page.`
        : `${
            typeLabel ?? 'This carousel'
          } has nothing to show for this store, so it won't appear on the published page.`;
      return (
        <EditorCarouselSlot>
          <EmptyCarouselNotice data-empty-notice>
            {isRecommendedForYou
              ? 'Recommended For You is personalized for each shopper and appears only when recommendations are available.'
              : emptyMessage}
          </EmptyCarouselNotice>
          {carousel}
        </EditorCarouselSlot>
      );
    }

    // Types without a renderer yet render nothing on the storefront — the same shape
    // a carousel already produces when its products query comes back empty, rather
    // than a new empty state shoppers can see.
    if (!puck.isEditing) {
      return <></>;
    }

    return (
      <EditorPlaceholder>
        <PlaceholderHeading>{resolvedHeading ?? UNTITLED_PLACEHOLDER_HEADING}</PlaceholderHeading>
        <span>
          {typeLabel === undefined
            ? 'Choose Products to show in the settings panel.'
            : `${typeLabel} carousels are not supported yet.`}{' '}
          This block will not render on the published page.
        </span>
      </EditorPlaceholder>
    );
  },
};
